import { useCallback, useContext, useEffect, useMemo, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'next-i18next';
import { Controller, useForm, FormProvider, useFormContext } from 'react-hook-form';
import { Country } from 'react-phone-number-input/input';
import debounce from 'lodash.debounce';
import { useAtom, useSetAtom } from 'jotai';

import { Button as ButtonV2, Combobox, CountriesMenu, Heading } from '@restworld/ui-ds-base';
import { Company } from '@restworld/utility-types';

import { EmployerCompanyModalFooterContext } from './employer-company-modal-footer-context';
import useEmployerCompanySearchMutation, {
  useEmployerCompanySearchMutationCacheData,
} from '../../hooks/use-employer-company-search-mutation';
import { cn } from '@restworld/utils-common';
import {
  currentCompanyActionAtom,
  lastCompanySearchValueAtom,
  selectedCompanyAtom,
} from '../../employer-companies.atoms';

const DEFAULT_COUNTRY: Country = 'IT';

export type CompanySearchRef = {
  submit: () => void;
};

const CompanySearch = forwardRef<CompanySearchRef, { hideCta?: boolean }>(({ hideCta }, ref) => {
  const { t } = useTranslation('payment-and-invoices');
  const methods = useForm<{ name_or_vat: string }>();
  const { handleSubmit } = methods;
  const footerContext = useContext(EmployerCompanyModalFooterContext);

  const [, setSelectedCompany] = useAtom(selectedCompanyAtom);
  const setCurrentCompanyAction = useSetAtom(currentCompanyActionAtom);
  const { getCacheData } = useEmployerCompanySearchMutationCacheData();

  const handleCancel = useCallback(() => setCurrentCompanyAction('chooseCompanyType'), [setCurrentCompanyAction]);

  const onSubmit = useCallback(
    (data: { name_or_vat: string }) => {
      const mutationData = getCacheData();
      const company = mutationData?.find((c) => c.vatNumber === data.name_or_vat || c.companyName === data.name_or_vat);

      const companyData: Partial<Company> = {
        name: company?.companyName,
        vat_number: company?.vatNumber,
        street_name: company?.streetName,
        country_code: company?.countryCode,
        province_code: company?.provinceCode,
        house_number: company?.houseNumber,
        zip_code: company?.zipCode,
        city: company?.city,
      };

      setSelectedCompany(companyData);
      setCurrentCompanyAction('createCompany');
    },
    [getCacheData, setCurrentCompanyAction, setSelectedCompany]
  );

  useImperativeHandle(ref, () => ({
    submit: () => {
      handleSubmit(onSubmit)();
    },
  }));

  const footerEl = useMemo(
    () => (
      <FooterButtonsWrapper>
        <ButtonV2 variant="tertiary" type="button" onClick={handleCancel}>
          {t('cancel')}
        </ButtonV2>
        <ButtonV2 type="button" onClick={handleSubmit(onSubmit)}>
          {t('next')}
        </ButtonV2>
      </FooterButtonsWrapper>
    ),
    [handleCancel, handleSubmit, onSubmit, t]
  );

  useEffect(() => {
    if (footerContext) {
      footerContext.setFooter(footerEl);
      return () => footerContext.setFooter(null);
    }
  }, [footerContext, footerEl]);

  return (
    <FormProvider {...methods}>
      <StyledForm autoComplete="off">
        <div>
          <Heading color="neutral" className="text-lg text-center w-full mb-10 lg:mb-16">
            {t('search_company_name_or_vat_below')}
          </Heading>
          <CompanyNameOrVatInput />
        </div>
        {!hideCta && !footerContext && (
          <ActionButtonsWrapper>
            <ButtonV2 variant="tertiary" type="button" onClick={handleCancel}>
              {t('cancel')}
            </ButtonV2>
            <ButtonV2 type="button" onClick={handleSubmit(onSubmit)}>
              {t('next')}
            </ButtonV2>
          </ActionButtonsWrapper>
        )}
      </StyledForm>
    </FormProvider>
  );
});

CompanySearch.displayName = 'CompanySearch';
export default CompanySearch;

interface CompanyNameOrVatInputProps {
  onCountryCodeChange?: (code: string) => void;
  onCompanySelect?: (company: Partial<Company>) => void;
  fieldName?: string;
  useLastSearchAtom?: boolean;
  fullWidth?: boolean;
}

export function CompanyNameOrVatInput({
  onCountryCodeChange,
  onCompanySelect,
  fieldName = 'name_or_vat',
  useLastSearchAtom = true,
  fullWidth = false,
}: CompanyNameOrVatInputProps) {
  const { t } = useTranslation('payment-and-invoices');
  const { control } = useFormContext();
  const [countryCode, setCountryCode] = useState<Country>(DEFAULT_COUNTRY);
  const [comboboxQueryValue, setComboboxQueryValue] = useState('');
  const { mutate, data: searchResults, isPending } = useEmployerCompanySearchMutation();
  const { getCacheData } = useEmployerCompanySearchMutationCacheData();
  const [lastSearch, setLastSearch] = useAtom(lastCompanySearchValueAtom);
  const hasSearchedRef = useRef(false);

  const handleCountryClick = useCallback(
    (value: string) => {
      setCountryCode(value as Country);
      onCountryCodeChange && onCountryCodeChange(value as Country);
    },
    [onCountryCodeChange]
  );

  const noOptionsMessageEl = useMemo(
    () => (
      <NoOptionsMessageWrapper>
        {comboboxQueryValue && hasSearchedRef.current && (
          <>
            <span>{t('we_cant_find_the_company_you_are_searching_for')}.</span>
            <span>{t('click_on_next_to_add_the_company_manually')}</span>
          </>
        )}
      </NoOptionsMessageWrapper>
    ),
    [comboboxQueryValue, t]
  );

  const debouncedMutate = useMemo(
    () =>
      debounce((queryValue) => {
        mutate({ countryCode, searchText: queryValue });
        hasSearchedRef.current = true;
      }, 500),
    [countryCode, mutate]
  );

  const dropdownList = useMemo(() => searchResults?.data.map((v) => v.companyName) || [], [searchResults?.data]);

  useEffect(() => {
    if (comboboxQueryValue) {
      debouncedMutate(comboboxQueryValue);
    }

    return () => {
      debouncedMutate.cancel();
    };
  }, [comboboxQueryValue, debouncedMutate]);

  const handleCompanyChange = useCallback(
    (nameOrVat: string) => {
      if (onCompanySelect) {
        const mutationData = getCacheData();
        const company = mutationData?.find((c) => c.vatNumber === nameOrVat || c.companyName === nameOrVat);

        if (company) {
          const companyData: Partial<Company> = {
            id: company?.id,
            name: company?.companyName,
            vat_number: company?.vatNumber,
            street_name: company?.streetName,
            country_code: company?.countryCode,
            province_code: company?.provinceCode,
            house_number: company?.houseNumber,
            zip_code: company?.zipCode,
            city: company?.city,
          };
          onCompanySelect(companyData);
        }
      }
    },
    [getCacheData, onCompanySelect]
  );

  return (
    <div className={cn(fullWidth ? 'w-full' : 'max-w-[640px] mx-auto')}>
      <label className="block text-base font-medium text-neutral-700 mb-2">{t('company_name_or_vat')}</label>
      <Controller
        name={fieldName}
        control={control}
        rules={{ required: comboboxQueryValue ? false : t('company_name_or_vat_is_required') }}
        defaultValue={useLastSearchAtom ? lastSearch : undefined}
        render={({ field, fieldState: { error } }) => (
          <div>
            {/* Input group: country selector + combobox share one border */}
            <div
              className={cn(
                'flex items-center border rounded-lg bg-neutral-10 h-12 hover:bg-neutral-30',
                error ? 'border-red-500 bg-red-50' : 'border-neutral-90'
              )}
            >
              {/* Country flag button */}
              <div className="flex items-center px-2 flex-shrink-0 border-r border-neutral-200 h-7">
                <CountriesMenu onChange={handleCountryClick} value={countryCode} favorites={['IT']} />
              </div>
              {/* Combobox without its own border */}
              <div className="flex-1 min-w-0">
                <Combobox
                  dropdownList={dropdownList}
                  placeholder={t('insert_the_name_or_vat')}
                  inputStyle="!border-0 !bg-transparent hover:!bg-transparent !rounded-none !shadow-none focus-visible:!ring-0 focus-visible:!ring-offset-0 pl-3"
                  listStyle="max-w-full mt-2 shadow-[0px_4px_16px_0px_rgba(0,0,26,0.50)]"
                  showIcon={false}
                  noOptionsMessage={noOptionsMessageEl}
                  value={field.value}
                  onChange={(nameOrVat: string) => {
                    field.onChange(nameOrVat);
                    if (useLastSearchAtom) {
                      setLastSearch(nameOrVat);
                    }
                    handleCompanyChange(nameOrVat);
                  }}
                  noFilter
                  isLoading={isPending}
                  autoCompleteOff
                  onQueryChange={(query) => {
                    setComboboxQueryValue(query);
                  }}
                  fullwidth
                />
              </div>
            </div>
            {error?.message && <p className="text-sm text-red-500 mt-1">{error.message}</p>}
          </div>
        )}
      />
    </div>
  );
}

const StyledForm = ({ className, ...props }: React.FormHTMLAttributes<HTMLFormElement>) => (
  <form className={cn('flex flex-col justify-between h-full', className)} {...props} />
);
const ActionButtonsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex justify-between items-center mt-10 lg:mt-8 pb-4 md:pb-0', className)} {...props} />
);
const FooterButtonsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex flex-row gap-3 w-full justify-center py-4 px-8 [&>button]:flex-1 [&>button]:md:flex-none',
      className
    )}
    {...props}
  />
);
const NoOptionsMessageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center', className)} {...props} />
);
